import type { GetMessagesResponseItem } from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import { globalEvents } from "src/constants/globalEvents";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import parseJwt from "src/lib/parseJwt";
import reportErrorSentry from "src/lib/reportErrorSentry";
import * as SendingMessagesTracker from "./SendingMessagesTracker";
import { StorageController } from "../StorageBloc/StorageBloc";
import { authenticationState } from "../state";
import { WebSocketConnection } from "./WebSocketConnection";
import { RateLimiter } from "./RateLimiter";
import * as WebSocketAuthTracker from "./WebSocketAuthTracker";

export enum WebsocketMessageType {
  ping = "ping",
  authenticate = "auth.v1.authenticate",
  authenticateSuccess = "auth.v1.authenticate-success",
  authenticateFailed = "auth.v1.authenticate-failed",
  unauthorized = "websocket.v1.error.unauthorized",
  sendMessage = "messaging.v1.send-message",
  receivedMessage = "messaging.v1.received-message",
  taskCreated = "tasks.v1.task_created",
  taskStatusUpdated = "tasks.v1.task_status_updated",
  taskUpdated = "tasks.v1.task_updated",
  notificationCreated = "notification-messages.v1.created",
  conversationMessageAdded = "conversation.v1.message-added",
  lifeLineItemSaved = "lifeline.v1.item_saved"
}

export enum WebsocketLifecycleEvent {
  connected = "connected",
  disconnected = "disconnected"
}

export interface WebsocketTaskStatusUpdatedPayload {
  group: string;
  newStatus: string;
  oldStatus: string;
  program: string;
  slug: string;
  taskId: string;
  updatingUserId: string;
  userId: string;
}

export interface WebsocketTaskUpdatedPayload {
  taskId: string;
  program: string;
  group: string;
  slug: string;
  status?: string;
  availableFrom?: string;
  availableTo?: string;
  startedAt?: string;
  completedAt?: string;
  additionalData?: Record<string, unknown>;
  uiElements?: Record<string, unknown>;
  behavior?: Record<string, unknown>;
  updatingUserId: string;
  userId: string;
}

export type WebsocketPayload =
  | GetMessagesResponseItem
  | WebsocketTaskStatusUpdatedPayload
  | WebsocketTaskUpdatedPayload
  | object;

export interface WebsocketMessage {
  type: WebsocketMessageType;
  payload: WebsocketPayload;
}

export type ObserverCallback = (
  data?: WebsocketMessage | { type: WebsocketLifecycleEvent; payload?: unknown }
) => void;

interface WebSocketObservers {
  id: string;
  type: WebsocketLifecycleEvent | WebsocketMessageType;
  callback: ObserverCallback;
}

export interface AddWebSocketObserverResponse {
  id: string;
  remove: () => void;
}

const MESSAGE_QUEUE_KEY = "message-queue";

// Message metadata keys
export const MESSAGE_METADATA_KEYS = {
  LOCAL_ID: "message:local-id",
  SEND_TIMESTAMP: "message:send-timestamp",
  RETRY_COUNT: "message:retry-count",
  LAST_RETRY_TIME: "message:last-retry-time",
  FIRST_ATTEMPT_TIME: "message:first-attempt-time",
  EXPIRED: "message:expired",
  EXPIRED_AT: "message:expired-at",
  AGE_HOURS: "message:age-hours",
  FAILED_AT: "message:failed-at",
  FINAL_RETRY_COUNT: "message:final-retry-count"
} as const;

export default class WebSocketBloc extends Cubit<null> {
  observers: WebSocketObservers[] = [];
  messageQueue: WebsocketMessage[] = [];
  websocket: WebSocketConnection | null = null;
  authenticatedWithToken: string | null = null;
  authenticatedUserId: string | null = null;
  appIsActive = true;

  websocketConnectingPromise?: Promise<void>;
  private authenticationPromise?: Promise<void>;
  private rateLimiter = new RateLimiter(20, 1000, 10); // 20 messages per second, report every 10th violation

  /**
   * Tracks all event listeners registered on the WebSocket connection.
   * Used for cleanup to prevent listener accumulation bug.
   * @private
   * @since 1.x.x
   */
  private currentEventListeners: Array<{
    event: string;
    callback: EventListener;
  }> = [];

  log = (message: string, deets?: unknown) => {
    if (featureFlags.getFlag(FeatureFlagName.loggingWebsocket)) {
      // eslint-disable-next-line no-console
      console.warn(`[WS] ${message}`, deets ?? undefined);
    }

    logSentryBreadcrumb("websocket", [message, deets]);
  };

  constructor() {
    super(null);
    this.setupAuthenticationListeners();
  }

  /**
   * Setup listeners for authentication state changes
   */
  private setupAuthenticationListeners = (): void => {
    // Listen for user logout/clear events
    window.addEventListener(globalEvents.USER_CLEAR, this.handleUserLogout);
  };

  /**
   * Handle user logout - disconnect WebSocket and clean up
   */
  private handleUserLogout = (): void => {
    this.log("User logged out, disconnecting WebSocket");
    this.disconnect();

    // Clear message queue and user tracking on logout to prevent cross-user contamination
    this.messageQueue = [];
    this.authenticatedUserId = null;
    this.saveMessageQueue();

    // Clear global trackers to prevent cross-user contamination
    SendingMessagesTracker.clearSentMessages();
    WebSocketAuthTracker.clearAuthAttempts();
  };

  /**
   * ===== WEBSOCKET =====
   **/

  /**
   * Connect to the websocket and authenticate with current access token.
   * If no access token, does not connect. If token changes, re-authenticates.
   * Clean up any existing websocket connection first if needed.
   */
  connectAndAuthenticate = async (accessToken: string): Promise<void> => {
    if (!accessToken) {
      this.log(
        "No access token or not authenticated, disconnecting if connected"
      );
      this.disconnect();
      return;
    }
    // Start new connection and authentication

    const isConnected = this.websocket?.readyState === WebSocket.OPEN;

    if (!isConnected) {
      this.websocketConnectingPromise =
        this.websocketConnectingPromise ?? this.establishConnection();
      try {
        await this.websocketConnectingPromise;
      } finally {
        this.websocketConnectingPromise = undefined;
      }
    }

    // Try to authenticate with existing connection
    try {
      await this.authenticate(accessToken);
      return;
    } catch (e) {
      this.log("Failed to authenticate, disconnecting and reconnecting", e);
      this.disconnect();
      // Fall through to establish new connection and re-authenticate
    }

    // Establish new connection after authentication failure
    this.websocketConnectingPromise = this.establishConnection();
    try {
      await this.websocketConnectingPromise;
      await this.authenticate(accessToken);
    } finally {
      this.websocketConnectingPromise = undefined;
    }
  };

  /**
   * Internal method to establish WebSocket connection
   */
  private establishConnection = async (): Promise<void> => {
    this.log("Establishing new WebSocket connection");

    // Clean up existing connection
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    const WS = WebSocketConnection.getInstance();
    this.websocket = WS;
    this.addEventListeners();
    await WS.connect();
  };

  /**
   * Remove all registered event listeners from the WebSocket connection.
   * Called before adding new listeners to prevent accumulation.
   * @private
   * @see currentEventListeners
   * @see addEventListeners
   */
  private removeEventListeners = (): void => {
    if (this.currentEventListeners.length === 0) {
      return;
    }

    this.log("Removing event listeners", {
      count: this.currentEventListeners.length
    });

    for (const { event, callback } of this.currentEventListeners) {
      if (this.websocket) {
        this.websocket.removeEventListener(event, callback);
      }
    }

    this.currentEventListeners = [];
    this.log("All event listeners removed");
  };

  /**
   * Add event listeners to the WebSocket connection.
   * Automatically removes any existing listeners first to prevent accumulation.
   * @throws {Error} If websocket is not initialized
   */
  addEventListeners = (): void => {
    if (!this.websocket) {
      throw new Error("No websocket");
    }

    // CRITICAL: Remove any existing listeners first to prevent accumulation
    this.removeEventListeners();

    // Create listener references for tracking
    const messageListener = this.handleIncomingMessage;

    // Add listeners to WebSocket
    this.websocket.addEventListener("message", messageListener);

    // Track listeners for cleanup
    this.currentEventListeners = [
      { event: "message", callback: messageListener }
    ];

    this.log("Added event listeners", {
      count: this.currentEventListeners.length,
      events: this.currentEventListeners.map((l) => l.event)
    });
  };

  pauseSending = false;
  pauseSendingUntilAuthSuccess = () => {
    this.log("Pausing sending until auth success");
    this.pauseSending = true;
    const observer = this.addObserver(
      WebsocketMessageType.authenticateSuccess,
      () => {
        this.log("Resuming sending");
        observer.remove();
        this.pauseSending = false;
        void this.processMessageQueue();
      }
    );
  };

  /**
   * Disconnect from the WebSocket and clean up all resources.
   *
   * Cleanup order is intentional:
   * 1. Remove event listeners first (prevents handlers firing during cleanup)
   * 2. Reset connection state flags
   * 3. Close and null the WebSocket
   * 4. Reset authentication state
   * 5. Dispatch lifecycle event
   */
  disconnect = (): void => {
    this.log("Disconnecting from websocket");

    // CRITICAL: Clean up listeners first to prevent accumulation
    this.removeEventListeners();

    this.websocketConnectingPromise = undefined;
    this.authenticationPromise = undefined;

    // Clean up WebSocket connection (this will also stop ping interval)
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    // Reset authentication state
    this.authenticatedWithToken = null;
    this.pauseSending = false;

    this.dispatchLifecycleEvent(WebsocketLifecycleEvent.disconnected);
  };

  /**
   * Stringify and send a message to the websocket connection
   * @param message - The message to send
   * @throws Error - If the websocket is not connected
   */
  readonly sendMessageInWebsocket = async (message: WebsocketMessage) => {
    if (!this.websocket) {
      throw new Error("Websocket not connected");
    }

    // Extract local-id for duplicate prevention
    let localId: string | undefined;
    if ("metadata" in message.payload) {
      const { payload } = message;
      const rawLocalId = payload.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID];

      // Only use if it's a non-empty string
      if (typeof rawLocalId === "string" && rawLocalId.length > 0) {
        localId = rawLocalId;
      }
    }

    // Check if this message has already been sent
    // This prevents duplicates when the same message exists in both queues
    if (localId && SendingMessagesTracker.hasBeenSent(localId)) {
      this.log(`Message already sent, skipping:`, { localId });
      return;
    }

    // Mark message as sent - once marked, it will never be sent again
    if (localId && !SendingMessagesTracker.markAsSent(localId)) {
      this.log(`Message already marked as sent (race condition), skipping:`, {
        localId
      });
      return;
    }

    // Check rate limit and report to Sentry if exceeded
    if (this.rateLimiter.isExceeding()) {
      const stats = this.rateLimiter.getStats();
      this.log(`Rate limit exceeded:`, stats);

      // Report to Sentry with throttling to avoid spam
      if (this.rateLimiter.shouldReportToSentry()) {
        reportErrorSentry(new Error("WebSocket message rate limit exceeded"), {
          messageType: message.type,
          userId: this.authenticatedUserId,
          rateLimitStats: stats
        });
      }
    }

    this.log(`Sending message:`, message);

    const { type, payload } = message;
    this.websocket.send(JSON.stringify({ type, payload }));

    // Record message after sending
    this.rateLimiter.recordMessage();
  };

  authenticate = async (accessToken: string): Promise<void> => {
    const open = this.websocket?.readyState === WebSocket.OPEN;
    if (!open) {
      return;
    }

    if (!accessToken || this.authenticatedWithToken === accessToken) {
      return;
    }

    // If authentication is already in progress, return the existing promise
    if (this.authenticationPromise) {
      this.log("Authentication already in progress, waiting for completion");
      return this.authenticationPromise;
    }

    // Check if we can authenticate with this token (throttle rapid retries)
    if (!WebSocketAuthTracker.canAuthenticate(accessToken)) {
      this.log(
        "Authentication throttled - too many recent attempts with this token"
      );
      throw new Error("Authentication throttled");
    }

    const message = {
      type: WebsocketMessageType.authenticate,
      payload: {
        accessToken
      }
    };

    const handleError = (
      data:
        | WebsocketMessage
        | {
            type: WebsocketLifecycleEvent;
            payload?: unknown;
          }
        | undefined,
      authObservers: AddWebSocketObserverResponse[],
      reject: (reason?: unknown) => void,
      errorMessage: string
    ) => {
      this.log(errorMessage, data);
      authObservers.forEach((o) => o.remove());
      this.disconnect();
      void authenticationState.reportFaultyAccessToken();
      reject(new Error(errorMessage));
    };

    const promise = new Promise<void>((resolve, reject) => {
      // Add timeout to prevent hanging indefinitely
      const timeoutId = setTimeout(() => {
        this.log("Authentication timeout after 5 seconds");
        authObservers.forEach((o) => o.remove());
        reject(new Error("Authentication timeout"));
      }, 5000);

      const authObservers = [
        this.addObserver(WebsocketMessageType.authenticateSuccess, (data) => {
          clearTimeout(timeoutId);
          this.log("Authentication success", data);
          authObservers.forEach((o) => o.remove());

          // Parse the JWT to get the user ID
          const parsedToken = parseJwt(accessToken);
          const currentUserId = parsedToken.sub || null;

          // Check if the user has changed
          if (
            this.authenticatedUserId &&
            this.authenticatedUserId !== currentUserId
          ) {
            this.log(
              `User changed from ${this.authenticatedUserId} to ${currentUserId}, clearing message queue`
            );
            this.messageQueue = [];
            this.saveMessageQueue();
          }

          this.authenticatedWithToken = accessToken;
          this.authenticatedUserId = currentUserId;
          void this.processMessageQueue();
          resolve();
        }),
        this.addObserver(WebsocketMessageType.authenticateFailed, (data) => {
          clearTimeout(timeoutId);
          handleError(data, authObservers, reject, "Authentication failed");
        }),
        this.addObserver(WebsocketMessageType.unauthorized, (data) => {
          clearTimeout(timeoutId);
          handleError(data, authObservers, reject, "Unauthorized");
        })
      ];

      if (this.websocket) {
        this.log("Sending authentication message");
        this.websocket.send(JSON.stringify(message));
        // Mark that we attempted authentication with this token
        WebSocketAuthTracker.markAuthAttempt(accessToken);
      } else {
        clearTimeout(timeoutId);
        this.log("No websocket, not sending authentication message");
        reject(new Error("No WS open, not sending auth"));
      }
    });

    // Store the promise and clear it when done
    this.authenticationPromise = promise;
    promise
      .finally(() => {
        this.authenticationPromise = undefined;
      })
      .catch(() => {
        // Ignore errors - already handled in the promise
      });

    return promise;
  };

  /**
   * ===== INCOMING MESSAGES =====
   */

  /**
   * Handle incoming messages from the websocket connection
   * @param event - The message event
   */
  handleIncomingMessage = (ev: Event) => {
    const event = ev as MessageEvent;
    const isWebsocketMessage =
      typeof event.data === "string" && event.data.startsWith("{");

    if (!isWebsocketMessage) {
      this.log(`Incoming message [UNKNOWN DATA TYPE]:`, event.data);
      return;
    }

    const message = JSON.parse(event.data) as WebsocketMessage;
    this.log(`Incoming message:`, message);

    this.notifyObservers(message);
  };

  /**
   * ===== OUTGOING MESSAGES =====
   */

  /**
   * Send a message to the websocket connection
   */
  send = async (message: WebsocketMessage) => {
    await this.addMessageToQueue(message);
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      await this.processMessageQueue();
    } else {
      throw new Error("Websocket not connected");
    }
  };

  /**
   * Add a message to the message queue, and store it in local storage
   * @param message - The message to add to the queue
   */
  addMessageToQueue = async (message: WebsocketMessage) => {
    this.log(`Adding message to queue:`, message);
    let newQueue = [...this.messageQueue, message];

    try {
      // Deduplicate by message:local-id (allows sending same content multiple times)
      if ("metadata" in message.payload) {
        const { payload } = message;
        const localId = payload.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID];

        if (localId) {
          // Keep only the last occurrence of each unique local-id
          newQueue = newQueue.filter(
            (m, i, self) =>
              i ===
              self.findIndex((m2) => {
                // Both messages must have metadata to compare local-ids
                if (!("metadata" in m2.payload) || !("metadata" in m.payload)) {
                  return m === m2;
                }

                const m2Payload = m2.payload;
                const m2LocalId =
                  m2Payload.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID];
                const mPayload = m.payload;
                const mLocalId =
                  mPayload.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID];

                // Only compare local-ids if both exist
                return m2LocalId && mLocalId && m2LocalId === mLocalId;
              })
          );
        }
      }

      this.messageQueue = newQueue;
      this.saveMessageQueue();
    } catch (e) {
      reportErrorSentry(e, { message });
    }
  };

  removeLocalMessage = (message: WebsocketMessage) => {
    this.log("Manually removing messaage from queue", message);
    this.messageQueue = this.messageQueue.filter((m) => m !== message);
    this.saveMessageQueue();
  };

  saveMessageQueue = () => {
    if (StorageController.activeUserId) {
      StorageController.setItem(MESSAGE_QUEUE_KEY, this.messageQueueToKeep);
    }
  };

  get messageQueueToKeep(): string {
    // remove pings
    const messagesToSave = this.messageQueue.filter((m) => m.type !== "ping");
    return JSON.stringify(messagesToSave);
  }

  /**
   * Process the message queue, sending each message to the websocket connection
   * and removing it from the queue if successful
   */
  processMessageQueue = async () => {
    if (this.pauseSending) {
      this.log("Sending paused, not sending");
      return;
    }

    if (this.messageQueue.length > 0) {
      this.log(`Processing message queue, length: ${this.messageQueue.length}`);
    }

    const successfullyProcessed: WebsocketMessage[] = [];

    // Collect messages to remove during iteration
    for (const message of this.messageQueue) {
      try {
        await this.sendMessageInWebsocket(message);
        successfullyProcessed.push(message);
      } catch (e) {
        reportErrorSentry(e);
        // Message remains in queue for retry
      }
    }

    if (successfullyProcessed.length > 0) {
      this.messageQueue = this.messageQueue.filter(
        (m) => !successfullyProcessed.includes(m)
      );
      this.saveMessageQueue();
    }
  };

  /**
   * ===== OBSERVERS =====
   **/

  /**
   * Add an observer to the websocket connection for messages of lifecycle events
   * @param type - The type of message or event to listen for
   * @param callback - The callback to run when the event is triggered
   **/
  addObserver = (
    type: WebsocketLifecycleEvent | WebsocketMessageType,
    callback: ObserverCallback
  ): AddWebSocketObserverResponse => {
    const id = `${type}-${Date.now()}`;
    this.log(`Adding observer: `, {
      type,
      id
    });

    this.observers.push({
      id,
      type,
      callback
    });

    return {
      id,
      remove: () => {
        this.observers = this.observers.filter((o) => o.id !== id);
      }
    };
  };

  dispatchLifecycleEvent = (event: WebsocketLifecycleEvent) => {
    this.log(`Dispatching lifecycle event:`, { event });
    this.notifyObservers({
      type: event
    });
  };

  /**
   * Notify all observers of a message
   * @param data - The message to send to observers
   **/
  notifyObservers = (
    data: WebsocketMessage | { type: WebsocketLifecycleEvent }
  ) => {
    const observers = this.observers.filter((o) => o.type === data.type);
    if (observers.length > 0) {
      this.log(`Notifying observers: type: ${data.type}`, {
        observers,
        message: data
      });
      observers.forEach((o) => o.callback(data));
    }
  };

  /**
   * Cleanup method to properly dispose of WebSocket resources
   * Called when the bloc is destroyed or user logs out
   */
  cleanup = (): void => {
    this.log("Cleaning up WebSocketBloc");

    // Disconnect WebSocket
    this.disconnect();

    // Remove event listeners
    window.removeEventListener(globalEvents.USER_CLEAR, this.handleUserLogout);

    // Clear observers
    this.observers = [];

    // Clear message queue
    this.messageQueue = [];

    this.websocketConnectingPromise = undefined;
    this.authenticationPromise = undefined;
    this.authenticatedWithToken = null;
    this.authenticatedUserId = null;
    this.pauseSending = false;
  };
}
